/**
 * Plausible loader — privacy-first analytics gated by cookie consent.
 *
 * Reads the user's consent decision from localStorage (set by CookieConsent.js)
 * and only injects the Plausible script tags if `analytics: true`.
 *
 * Loads TWO scripts (the user's Plausible setup uses both):
 *   1. The paid-plan tracker (`pa-32VNxtAN3C60XPg-2YGqA.js`) which provides
 *      `plausible.init()` for advanced features.
 *   2. The standard `script.js` keyed by `data-domain`.
 *
 * Domain comes from REACT_APP_PLAUSIBLE_DOMAIN. If not set, defaults to
 * vinepass.co.za (registered with plausible.io).
 */
const STORAGE_KEY = 'vp_cookie_consent_v1';
const SCRIPT_ID = 'plausible-tracker';
const SCRIPT_PA_ID = 'plausible-pa-tracker';
const DOMAIN = process.env.REACT_APP_PLAUSIBLE_DOMAIN || 'vinepass.co.za';

const hasAnalyticsConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return !!parsed.analytics;
  } catch {
    return false;
  }
};

export const loadPlausible = () => {
  if (typeof window === 'undefined') return;
  if (!hasAnalyticsConsent()) return;
  if (document.getElementById(SCRIPT_ID) && document.getElementById(SCRIPT_PA_ID)) return;

  // 1. Paid-plan tracker (provides plausible.init for extensions)
  if (!document.getElementById(SCRIPT_PA_ID)) {
    const pa = document.createElement('script');
    pa.id = SCRIPT_PA_ID;
    pa.async = true;
    pa.src = 'https://plausible.io/js/pa-32VNxtAN3C60XPg-2YGqA.js';
    document.head.appendChild(pa);

    // Bootstrap queue + init exactly as documented
    window.plausible = window.plausible || function () {
      (window.plausible.q = window.plausible.q || []).push(arguments);
    };
    window.plausible.init = window.plausible.init || function (i) {
      window.plausible.o = i || {};
    };
    window.plausible.init();
  }

  // 2. Standard pageview tracker keyed by data-domain
  if (!document.getElementById(SCRIPT_ID)) {
    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.defer = true;
    s.dataset.domain = DOMAIN;
    s.src = 'https://plausible.io/js/script.js';
    document.head.appendChild(s);
  }
};

/** Track a custom event. No-op if consent hasn't been granted. */
export const trackEvent = (name, props) => {
  if (!hasAnalyticsConsent()) return;
  if (typeof window === 'undefined') return;
  if (typeof window.plausible !== 'function') return;
  window.plausible(name, props ? { props } : undefined);
};
